import React, { useState } from 'react';
import { RiEditLine, RiMailLine, RiPhoneLine } from 'react-icons/ri';
import {
  Button,
  Divider,
  Field,
  HelperText,
  IconButton,
  Label,
  Modal,
  TextInput,
} from '../../../../../components/UiComponents/Ui';
import { editSupplierInfoSchema } from './setupValidators';
import { submitEditSupplierInfo } from './setupActions';
import { getError, handleChange } from '../../../../helperFunctions';
import { useQuery } from '@tanstack/react-query';
import { getCountryCodes } from '../../../../../Actions/commonActions';
import MultiSelectDropdown from '../../../../../components/MultiSelectDropdown';
import { currencies, formateZodError } from '../../../../../Utlils';
import SubmitButton from '../../../../../components/UiComponents/SubmitButton';
import { useGlobalContext } from '../../../../../Contexts/GlobalContext';

const EditSupplierInfo = ({
  supplierID,
  supplierDetailsMutate,
  supplierDetails,
}) => {
  const { setSnackbar } = useGlobalContext();
  const [editSupplierInfo, setEditSupplierInfo] = useState(false);
  const [supplierData, setSupplierData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const onChange = (path, value) =>
    handleChange(path, value, setSupplierData, setFormErrors, formErrors);

  const handleClose = () => {
    setEditSupplierInfo(false);
    setSupplierData({});
    setFormErrors({});
  };

  const {
    data: countryCodesData,
    isLoading: countryCodesDataLoading,
    error: countryCodesDataError,
  } = useQuery({
    queryKey: ['country-codes'],
    queryFn: getCountryCodes,
  });

  const countryCodeOptions = (countryCodesData || []).map((country) => ({
    label: `${country?.icon} +${country?.countryCode} - ${country?.label}`,
    value: `+${country?.countryCode}`,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const validationResult = editSupplierInfoSchema.safeParse({
        ...supplierData,
        _id: supplierID,
      });

      if (validationResult.success) {
        const response = await submitEditSupplierInfo({
          ...validationResult.data,
          _id: supplierID,
        });
        if (!response?.error) {
          setSnackbar({
            open: true,
            msg: 'Supplier Details updated successfully!',
            severity: 'success',
          });
          supplierDetailsMutate();
          handleClose();
        } else {
          setSnackbar({
            open: true,
            msg: 'Internal Server Error! Please try again!',
            severity: 'error',
          });
        }
      } else {
        setFormErrors(formateZodError(validationResult.error.errors));
      }
    } catch (error) {
      console.log('error in handleSubmit from edit Supplier info', error);
      setSnackbar({
        open: true,
        msg: 'Internal Server Error! Please try again!',
        severity: 'error',
      });
    }
  };

  const codeError = getError(['phone', 'countryCode'], formErrors);
  const numberError = getError(['phone', 'number'], formErrors);

  return (
    <>
      <IconButton
        aria-label="Edit supplier details"
        variant="ghost"
        onClick={() => {
          setEditSupplierInfo(true);
          setSupplierData({
            supplierName: supplierDetails?.supplierName,
            email: supplierDetails?.email,
            phone: supplierDetails?.phone,
            description: supplierDetails?.description,
            currency: supplierDetails?.currency,
            invoicePrefix: supplierDetails?.invoicePrefix,
          });
        }}
      >
        <RiEditLine size={20} />
      </IconButton>

      <Modal
        showModal={editSupplierInfo}
        setShowModal={handleClose}
        closeOnOutsideClick={false}
        width={660}
      >
        <form className="space-y-[18px]" onSubmit={handleSubmit}>
          <h2 className="text-lg font-semibold text-gray-800">
            Edit Supplier Details
          </h2>
          <Divider />

          {/* name & email */}
          <div className="grid grid-cols-2 items-start gap-4">
            <Field
              label="Supplier Name"
              required
              error={getError(['supplierName'], formErrors)}
            >
              <TextInput
                placeholder="Enter Supplier Name"
                value={supplierData?.supplierName || ''}
                error={getError(['supplierName'], formErrors)}
                onChange={(e) => onChange(['supplierName'], e.target.value)}
              />
            </Field>
            <Field
              label="Supplier Email"
              required
              error={getError(['email'], formErrors)}
            >
              <TextInput
                type="email"
                startIcon={<RiMailLine size={16} className="text-gray-500" />}
                placeholder="Enter Supplier Email"
                value={supplierData?.email || ''}
                error={getError(['email'], formErrors)}
                onChange={(e) => onChange(['email'], e.target.value)}
              />
            </Field>
          </div>

          {/* phone */}
          <div className="space-y-[6px]">
            <Label>Contact Number</Label>
            <div className="flex items-start gap-[10px]">
              <div className="w-[190px] shrink-0">
                <MultiSelectDropdown
                  options={countryCodeOptions}
                  disabled={Boolean(
                    countryCodesDataLoading || countryCodesDataError,
                  )}
                  dropdownText="Country Code"
                  width="190px"
                  search
                  selected={[
                    {
                      label:
                        countryCodeOptions.find(
                          (d) => d.value === supplierData?.phone?.countryCode,
                        )?.label || '',
                      value: supplierData?.phone?.countryCode || '',
                    },
                  ]}
                  setSelected={(e) => {
                    const value = e;
                    onChange(['phone', 'countryCode'], value?.[0]?.value);
                  }}
                />
              </div>
              <div className="w-full">
                <TextInput
                  type="number"
                  placeholder="Enter 10 digit number"
                  startIcon={
                    <RiPhoneLine size={16} className="text-gray-500" />
                  }
                  value={supplierData?.phone?.number || ''}
                  error={numberError}
                  onChange={(e) =>
                    onChange(['phone', 'number'], e.target.value)
                  }
                />
              </div>
            </div>
            {!!codeError && <HelperText>{codeError}</HelperText>}
            {!!numberError && <HelperText>{numberError}</HelperText>}
          </div>

          {/* currency & invoice prefix */}
          <div className="grid grid-cols-2 items-start gap-4">
            <Field
              label="Default Currency"
              required
              error={getError(['currency'], formErrors)}
            >
              <MultiSelectDropdown
                search
                options={currencies}
                dropdownText="Select Default Currency"
                selected={[
                  {
                    label: supplierData?.currency?.label || '',
                    value: supplierData?.currency?.value || '',
                  },
                ]}
                setSelected={(e) => {
                  const v = e;
                  onChange(['currency'], {
                    label: v?.[0]?.label,
                    value: v?.[0]?.value,
                    symbol: v?.[0]?.symbol,
                  });
                }}
              />
            </Field>
            <Field
              label="Invoice Prefix"
              error={getError(['invoicePrefix'], formErrors)}
            >
              <TextInput
                placeholder="Enter Invoice Prefix"
                value={supplierData?.invoicePrefix || ''}
                error={getError(['invoicePrefix'], formErrors)}
                onChange={(e) => onChange(['invoicePrefix'], e.target.value)}
              />
            </Field>
          </div>

          {/* description */}
          <Field
            label="Description"
            error={getError(['description'], formErrors)}
          >
            <TextInput
              multiline
              rows={4}
              placeholder="Enter Description"
              value={supplierData?.description || ''}
              error={getError(['description'], formErrors)}
              onChange={(e) => onChange(['description'], e.target.value)}
            />
          </Field>

          <Divider />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outlined" intent="default" onClick={handleClose}>
              Cancel
            </Button>
            <SubmitButton name="Save Changes" />
          </div>
        </form>
      </Modal>
    </>
  );
};

export default EditSupplierInfo;
